import { FetchPolicy } from '@apollo/client';
import { ThemeProvider as MuiThemeProvider } from '@mui/material';
import { alpha, darken, getContrastRatio, lighten } from '@mui/material/styles';
import omitBy from 'lodash/omitBy';
import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import UserRole from 'enums/UserRole';

import {
  ThemeDetailsFragment,
  useGetOrganizationThemeForOrganizationAdminLazyQuery,
  useGetOrganizationThemeForStudentLazyQuery,
  useGetOrganizationThemeForTeacherLazyQuery,
} from 'graphql/types/generated';

import { getUserId, getUserRole } from 'redux/user';

import {
  DEFAULT_COLOR_PALETTE,
  SYLLABIRD_THEME,
  defaultValue,
} from './constants';
import { createMuiTheme } from './createMuiTheme';
import setColors from './setColors';
import setFonts from './setFonts';
import {
  AdjustedColor,
  ColorPalette,
  ConfigurableTheme,
  Theme,
  ThemeContextType,
} from './types';

export const ThemeContext = createContext<ThemeContextType>(defaultValue);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [theme, setTheme] = useState<Theme>(SYLLABIRD_THEME);
  const [isPreviewingTheme, setIsPreviewingTheme] = useState<boolean>(false);
  const [isThemeLoading, setIsThemeLoading] = useState(true);
  const [themeId, setThemeId] = useState<string | null>(null);

  const muiTheme = useMemo(() => createMuiTheme(theme), [theme]);

  const adjustColorForDisplay = useCallback(
    (color: string): AdjustedColor => {
      const isDarkTheme =
        getContrastRatio(theme.contentBackground, '#ffffff') > 3;
      const isLightColor = getContrastRatio(color, '#ffffff') < 3;

      if (isDarkTheme) {
        return {
          background: alpha(lighten(color, 0.1), 0.35),
          foreground: lighten(color, 0.65),
        };
      }

      return {
        background: alpha(color, 0.25),
        foreground: isLightColor ? darken(color, 0.4) : darken(color, 0.2),
      };
    },
    [theme.contentBackground]
  );

  const userId = useSelector(getUserId);
  const userRole = useSelector(getUserRole);

  const applyThemeData = useCallback(
    (themeData: ThemeDetailsFragment | null | undefined) => {
      if (!themeData) {
        setTheme(SYLLABIRD_THEME);
        setColors(SYLLABIRD_THEME);
        setFonts(SYLLABIRD_THEME);
        return;
      }

      const themeOverrides = omitBy(themeData, (value) => !value);
      const colorPaletteOverrides = omitBy(
        themeData?.colorPalette,
        (value) => !value
      );

      const mergedColorPalette: ColorPalette = {
        ...DEFAULT_COLOR_PALETTE,
        ...colorPaletteOverrides,
      };

      const mergedTheme: Theme = {
        ...SYLLABIRD_THEME,
        ...themeOverrides,
        colorPalette: mergedColorPalette,
      };

      setTheme(mergedTheme);
      setColors(mergedTheme);
      setFonts(mergedTheme);
    },
    []
  );

  const processTheme = useCallback(
    (themeData?: ThemeDetailsFragment | null) => {
      applyThemeData(themeData);
      setThemeId(themeData?.id ?? null);
    },
    [applyThemeData]
  );

  const [getOrganizationThemeForTeacher] =
    useGetOrganizationThemeForTeacherLazyQuery({
      onError: () => {
        processTheme(null);
        setIsThemeLoading(false);
      },
    });

  const [getOrganizationThemeForStudent] =
    useGetOrganizationThemeForStudentLazyQuery({
      onError: () => {
        processTheme(null);
        setIsThemeLoading(false);
      },
    });

  const [getOrganizationThemeForOrganizationAdmin] =
    useGetOrganizationThemeForOrganizationAdminLazyQuery({
      onError: () => {
        processTheme(null);
        setIsThemeLoading(false);
      },
    });

  const getThemeData = useCallback(
    async (userId: string, userRole: UserRole, fetchPolicy?: FetchPolicy) => {
      if (userRole === UserRole.Teacher) {
        const { data } = await getOrganizationThemeForTeacher({
          variables: { teacherId: userId },
          fetchPolicy,
        });
        const orgTheme = data?.teacher?.organization?.themes?.[0];
        const userSelectedTheme = data?.teacher?.userSettings?.selectedTheme;
        processTheme(userSelectedTheme ?? orgTheme ?? null);
      } else if (userRole === UserRole.Student) {
        const { data } = await getOrganizationThemeForStudent({
          variables: { studentAccountId: userId },
          fetchPolicy,
        });
        const orgTheme =
          data?.studentAccount?.student?.teachers?.[0]?.teacher?.organization
            ?.themes?.[0];
        const userSelectedTheme =
          data?.studentAccount?.userSettings?.selectedTheme;
        processTheme(userSelectedTheme ?? orgTheme ?? null);
      } else if (userRole === UserRole.OrganizationAdmin) {
        const { data } = await getOrganizationThemeForOrganizationAdmin({
          variables: { organizationAdminId: userId },
          fetchPolicy,
        });
        processTheme(data?.organizationAdmin?.organization?.themes?.[0]);
      } else {
        processTheme(null);
      }
    },
    [
      getOrganizationThemeForTeacher,
      getOrganizationThemeForStudent,
      getOrganizationThemeForOrganizationAdmin,
      processTheme,
    ]
  );

  useEffect(() => {
    (async () => {
      if (userId && userRole) {
        await getThemeData(userId, userRole);
        setIsThemeLoading(false);
      }
    })();
  }, [userId, userRole, getThemeData]);

  const previewTheme = useCallback(
    (themeOverrides: Partial<ConfigurableTheme>) => {
      const mergedTheme = {
        ...theme,
        ...themeOverrides,
        colorPalette: {
          ...theme.colorPalette,
          ...themeOverrides.colorPalette,
        },
      };

      setTheme(mergedTheme);
      setColors(mergedTheme);
      setFonts(mergedTheme);
      setIsPreviewingTheme(true);
    },
    [theme]
  );

  const previewThemeFromData = useCallback(
    (themeData: ThemeDetailsFragment | null) => {
      applyThemeData(themeData);
      setIsPreviewingTheme(true);
    },
    [applyThemeData]
  );

  const refreshTheme = useCallback(async () => {
    setIsPreviewingTheme(false);
    toast.dismiss('previewTheme');
    if (userId && userRole) {
      await getThemeData(userId, userRole, 'network-only');
    }
  }, [userId, userRole, getThemeData]);

  return (
    <ThemeContext.Provider
      value={{
        loading: isThemeLoading,
        theme,
        themeId,
        isPreviewingTheme,
        setIsPreviewingTheme,
        previewTheme,
        previewThemeFromData,
        refreshTheme,
        adjustColorForDisplay,
      }}
    >
      <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
